import { type RefObject, useEffect } from "react";

/**
 * Locks background scroll and marks #app-content as inert while a modal is open.
 * Pair with createPortal(..., document.body) so the dialog is not a descendant of the inert tree.
 */
export function useModalA11y(open: boolean, modalRef: RefObject<HTMLElement | null>) {
	useEffect(() => {
		if (!open) {
			return;
		}

		const originalOverflow = document.body.style.overflow;
		document.body.style.overflow = "hidden";

		const appContent = document.getElementById("app-content");
		if (appContent) {
			appContent.inert = true;
		}

		const frame = requestAnimationFrame(() => {
			modalRef.current?.focus();
		});

		const handleKeyDown = (e: KeyboardEvent) => {
			if (e.key !== "Tab" || !modalRef.current) {
				return;
			}

			const focusable = modalRef.current.querySelectorAll<HTMLElement>(
				"button, [href], input, select, textarea, [tabindex]:not([tabindex=\"-1\"])"
			);
			if (!focusable.length) {
				return;
			}

			const first = focusable[0];
			const last = focusable[focusable.length - 1];
			if (e.shiftKey) {
				if (document.activeElement === first) {
					e.preventDefault();
					last.focus();
				}
			}
			else if (document.activeElement === last) {
				e.preventDefault();
				first.focus();
			}
		};

		document.addEventListener("keydown", handleKeyDown);
		return () => {
			cancelAnimationFrame(frame);
			document.body.style.overflow = originalOverflow;
			if (appContent) {
				appContent.inert = false;
			}
			document.removeEventListener("keydown", handleKeyDown);
		};
	}, [open, modalRef]);
}
